/**
 * 斗兽棋棋盘组件
 * 9×7棋盘，8种动物，河流、陷阱、巢穴等地形
 */

import React from 'react';
import { JungleGameState, Position, JungleAnimal, AnimalType, TerrainType } from '../types/game';
import { ANIMAL_EMOJIS, ANIMAL_NAMES, ANIMAL_RANKS } from '../types/game';
import { cn } from '../lib/utils';

interface JungleBoardProps {
  gameState: JungleGameState;
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  enlarged?: boolean;
  gameMode?: 'pvp' | 'pvc';
}

const JungleBoard: React.FC<JungleBoardProps> = ({
  gameState,
  onCellClick,
  disabled = false,
  enlarged = false,
  gameMode = 'pvp'
}) => {
  const board = gameState.board;
  const terrain = gameState.terrain;

  const getCellSize = (): string => {
    return enlarged ? 'w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20' : 'w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16';
  };

  const getAnimalSize = (): string => {
    return enlarged ? 'text-2xl sm:text-3xl lg:text-4xl' : 'text-xl sm:text-2xl lg:text-3xl';
  };

  const getTerrainStyle = (row: number, col: number): string => {
    const baseStyle = getCellSize();
    const currentTerrain = terrain[row][col];
    const animal = board[row][col];


    // 巢穴样式 - 美化版本
    if (currentTerrain.type === 'den') {
      if (currentTerrain.owner === 'black') {
        return `${baseStyle} bg-gradient-to-br from-amber-600 via-amber-700 to-amber-800 sm:border-3 border-amber-900 shadow-inner relative overflow-hidden`;
      } else {
        return `${baseStyle} bg-gradient-to-br from-indigo-600 via-blue-700 to-blue-800 sm:border-3 border-blue-900 shadow-inner relative overflow-hidden`;
      }
    }

    // 陷阱样式 - 美化版本
    if (currentTerrain.type === 'trap') {
      if (currentTerrain.owner === 'black') {
        return `${baseStyle} bg-gradient-to-br from-orange-300 via-red-400 to-red-500 sm:border-2 border-red-600 shadow-lg relative overflow-hidden`;
      } else {
        return `${baseStyle} bg-gradient-to-br from-cyan-300 via-blue-400 to-blue-500 sm:border-2 border-blue-600 shadow-lg relative overflow-hidden`;
      }
    }

    // 河流样式 - 美化版本
    if (currentTerrain.type === 'river') {
      return `${baseStyle} bg-gradient-to-br from-blue-400/80 via-cyan-500/80 to-blue-600/70 sm:border-2 border-blue-700 shadow-inner relative overflow-hidden`;
    }

    // 普通格子 - 美化版本
    return `${baseStyle} bg-gradient-to-br from-green-300 via-green-200 to-emerald-100 sm:border-2 border-green-600 shadow-sm`;
  };

  const getCellStyle = (row: number, col: number): string => {
    const baseStyle = getTerrainStyle(row, col);
    const isPossibleMove = gameState.possibleMoves?.some(move => move.to.row === row && move.to.col === col) || false;
    const isSelected = gameState.selectedPiece?.row === row && gameState.selectedPiece?.col === col;

    // 隐藏选中效果（发光显示在错误位置）
    if (isSelected) {
      return baseStyle;
    }

    // 可能移动位置
    if (isPossibleMove) {
      const move = gameState.possibleMoves?.find(m => m.to.row === row && m.to.col === col);
      const isCapture = move?.captures && move.captures.length > 0;
      const isRiverJump = move?.isRiverJump || false;

      if (isCapture) {
        return `${baseStyle} ring-1 sm:ring-4 ring-red-500 ring-opacity-90 animate-pulse z-10`;
      } else if (isRiverJump) {
        return `${baseStyle} ring-1 sm:ring-4 ring-yellow-600 ring-opacity-90 animate-pulse z-10`;
      } else {
        return `${baseStyle} ring-1 sm:ring-4 ring-yellow-400 ring-opacity-70 z-10`;
      }
    }

    // 悬停效果
    if (!disabled) {
      return `${baseStyle} hover:scale-105 transition-all duration-200 cursor-pointer`;
    }

    return baseStyle;
  };

  const getAnimalStyle = (animal: JungleAnimal, isSelected: boolean, isLastMove: boolean): string => {
    const baseStyle = 'flex items-center justify-center rounded-full shadow-lg transition-all duration-300';
    const sizeStyle = 'w-4/5 h-4/5';
    const lastMoveStyle = isLastMove ? 'ring-2 ring-yellow-400 ring-offset-1' : '';

    if (animal.player === 'black') {
      return `${baseStyle} ${sizeStyle} ${lastMoveStyle} bg-gradient-to-br from-gray-800 to-black text-white sm:border-2 border-gray-600`;
    } else {
      return `${baseStyle} ${sizeStyle} ${lastMoveStyle} bg-gradient-to-br from-white to-gray-100 text-black sm:border-2 border-gray-300`;
    }
  };

  const renderAnimal = (animal: JungleAnimal | null, row: number, col: number) => {
    if (!animal) return null;

    const isSelected = gameState.selectedPiece?.row === row && gameState.selectedPiece?.col === col;
    const isLastMove = lastMovePos?.row === row && lastMovePos?.col === col;
    const isCurrentPlayerAnimal = animal.player === gameState.currentPlayer;

    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className={cn(
          getAnimalStyle(animal, isSelected, isLastMove),
          {
            'scale-125 ring-1 sm:ring-4 ring-blue-400 ring-opacity-70 shadow-xl': isSelected, // 选中时放大并加强效果
            'ring-1 sm:ring-2 ring-yellow-400 ring-opacity-60': isLastMove && !isSelected, // 最后一步但未选中
            'ring-1 ring-opacity-30': isCurrentPlayerAnimal && !isSelected && !isLastMove, // 当前玩家棋子
            'ring-yellow-300': isCurrentPlayerAnimal && animal.player === 'black' && !isSelected && !isLastMove,
            'ring-blue-300': isCurrentPlayerAnimal && animal.player === 'white' && !isSelected && !isLastMove,
            'opacity-85': !isCurrentPlayerAnimal && !isSelected // 非当前玩家棋子透明度
          }
        )}>
          <div className={cn(
            getAnimalSize(),
            {
              'text-lg sm:text-xl lg:text-2xl': isSelected // 选中时图标也放大
            }
          )}>
            {ANIMAL_EMOJIS[animal.type]}
          </div>
          {/* 动物等级标记 
          <div className={cn(
            "absolute top-0 right-0 font-bold bg-yellow-400 text-black rounded-full flex items-center justify-center",
            isSelected ? "w-5 h-5 text-sm" : "w-4 h-4 text-xs" // 选中时等级标记也放大
          )}>
            {ANIMAL_RANKS[animal.type]}
          </div>
          */}
        </div>
      </div>
    );
  };

  const renderTerrainIndicator = (row: number, col: number) => {
    const currentTerrain = terrain[row][col];
    const animal = board[row][col];

    // 如果有动物，不显示地形指示器
    if (animal) return null;

    // 巢穴指示器 - 美化版本
    if (currentTerrain.type === 'den') {
      return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {/* 背景光晕效果 */}
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-200 to-amber-300 opacity-20 rounded-lg animate-pulse"></div>
          {/* 内部光辉纹理 */}
          <div className="absolute inset-1 rounded opacity-60"
               style={{background: 'radial-gradient(circle at center, rgba(251,191,36,0.4) 0%, transparent 70%)'}}></div>
          {/* 巢穴图标 */}
          <div className="relative z-10">
            <div className="text-3xl drop-shadow-lg"
                 style={{filter: 'drop-shadow(0 0 8px rgba(251,191,36,0.6))'}}>
              {currentTerrain.owner === 'black' ? '🏰' : '🏛️'}
            </div>
            {/* 闪耀星星装饰 */}
            <div className="absolute -top-1 -right-1 text-xs animate-pulse">✨</div>
            <div className="absolute -bottom-1 -left-1 text-xs animate-pulse" style={{animationDelay: '0.5s'}}>✨</div>
            <div className="absolute top-0 -left-2 text-xs animate-pulse" style={{animationDelay: '1s'}}>⭐</div>
            <div className="absolute bottom-0 -right-2 text-xs animate-pulse" style={{animationDelay: '1.5s'}}>⭐</div>
          </div>
          {/* 底部徽章 */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1">
            <div className={`text-xs px-2 py-1 rounded-full font-bold shadow ${
              currentTerrain.owner === 'black'
                ? 'bg-amber-600 text-white'
                : 'bg-blue-600 text-white'
            }`}>
              {currentTerrain.owner === 'black' ? '黑' : '白'}
            </div>
          </div>
        </div>
      );
    }

    // 陷阱指示器 - 美化版本
    if (currentTerrain.type === 'trap') {
      return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {/* 危险光晕效果 */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-orange-400 opacity-30 rounded-lg animate-pulse"></div>
          {/* 内部纹理 */}
          <div className="absolute inset-1 border border-red-300 opacity-50 rounded"
               style={{background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(254,202,202,0.3) 3px, rgba(254,202,202,0.3) 6px)'}}></div>
          {/* 陷阱图标 */}
          <div className="relative">
            <div className="text-2xl drop-shadow-lg">
              ⚠️
            </div>
            {/* 危险标记 */}
            <div className="absolute -top-1 -right-1 text-xs text-red-600 font-bold animate-pulse">!</div>
            {/* 小尖刺装饰 */}
            <div className="absolute -top-2 left-0 text-xs text-red-500">📍</div>
            <div className="absolute -bottom-2 right-0 text-xs text-red-500">📍</div>
          </div>
        </div>
      );
    }

    // 河流指示器 - 美化版本
    if (currentTerrain.type === 'river') {
      return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden rounded-lg">
          {/* 水波纹效果 */}
          <div className="absolute inset-0 opacity-40">
            <div className="text-4xl text-blue-200 animate-pulse" style={{animation: 'wave 3s ease-in-out infinite'}}>〜</div>
          </div>
          {/* 水滴装饰 */}
          <div className="absolute top-1 left-1 text-xs text-blue-300 opacity-60 animate-ping">💧</div>
          <div className="absolute bottom-1 right-1 text-xs text-blue-300 opacity-60 animate-ping" style={{animationDelay: '1s'}}>💧</div>
        </div>
      );
    }

    return null;
  };

  const renderMoveIndicator = (row: number, col: number) => {
    // 隐藏所有移动提示，因为它们显示在错误的位置
    return null;
  };

  const renderCoordinates = () => {
    // 禁用坐标显示以避免页面布局问题
    return null;
  };

  // 获取最后一步的移动标记位置
  const getLastMovePosition = (): Position | null => {
    if (gameState.moves.length === 0) return null;
    const lastMove = gameState.moves[gameState.moves.length - 1];
    return lastMove.to;
  };

  const lastMovePos = getLastMovePosition();

  return (
    <div className="flex flex-col items-center space-y-6">

      {/* 棋盘 */}
      <div className="relative bg-gradient-to-br from-green-300 to-green-600 p-1 sm:p-4 rounded-xl shadow-2xl">
        <div className="grid grid-cols-7 sm:gap-1 border-1 sm:border-4 border-green-800 rounded-lg">
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={cn(
                  getCellStyle(rowIndex, colIndex),
                  {
                    "cursor-not-allowed opacity-75": disabled
                  }
                )}
                onClick={() => {
                  if (disabled) return;
                  onCellClick({ row: rowIndex, col: colIndex });
                }}
              >
                {/* 动物 */}
                {renderAnimal(cell, rowIndex, colIndex)}

                {/* 地形指示器 */}
                {renderTerrainIndicator(rowIndex, colIndex)}

                {/* 移动指示器 */}
                {renderMoveIndicator(rowIndex, colIndex)}

                {/* 最后一步标记 - 已隐藏发光效果 */}
                {/* lastMovePos && lastMovePos.row === rowIndex && lastMovePos.col === colIndex && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-yellow-400 rounded-full opacity-80 animate-ping"></div>
                  </div>
                ) */}
              </div>
            ))
          )}
        </div>

        {/* 坐标标记 */}
        {renderCoordinates()}
      </div>
    </div>
  );
};

export default JungleBoard;